import {
  isInactiveObject,
  type ObjectStatusValue
} from "./objectStatus.helpers";

export type ReportObjectStatusSource = {
  uniqueId?: string;
  name?: string;
  aNumber?: string;
  serialNumber?: string;
  status?: ObjectStatusValue;
};

export type ReportObjectStatusLookups = {
  byUniqueId: Record<string, ObjectStatusValue>;
  byName: Record<string, ObjectStatusValue>;
  /** Device serials only — kept separate from byName to avoid name/serial collisions. */
  bySerial: Record<string, ObjectStatusValue>;
};

export type ReportInactiveRow = {
  uniqueId?: string;
  name?: string;
  object?: string;
  licensePlate?: string;
  device?: string;
  serialNumber?: string;
  aNumber?: string;
  status?: ObjectStatusValue;
  isInactive?: boolean;
  groupedInfo?: {
    uniqueId?: string;
    name?: string;
    serialNumber?: string;
  };
};

const normalizeLookupKey = (value?: string) =>
  value?.trim().toLowerCase() || "";

const indexStatusByKey = (
  target: Record<string, ObjectStatusValue>,
  key: string | undefined,
  status: ObjectStatusValue
) => {
  if (!key) return;
  target[key] = status;
  const normalized = normalizeLookupKey(key);
  if (normalized) target[normalized] = status;
};

const lookupStatusByCandidates = (
  candidates: Array<string | undefined>,
  map: Record<string, ObjectStatusValue>
): ObjectStatusValue | undefined => {
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (Object.prototype.hasOwnProperty.call(map, candidate)) {
      return map[candidate];
    }
    const normalized = normalizeLookupKey(candidate);
    if (normalized && Object.prototype.hasOwnProperty.call(map, normalized)) {
      return map[normalized];
    }
  }
  return undefined;
};

export const buildReportObjectStatusLookups = (
  objects: ReportObjectStatusSource[] | null | undefined
): ReportObjectStatusLookups => {
  const byUniqueId: Record<string, ObjectStatusValue> = {};
  const byName: Record<string, ObjectStatusValue> = {};
  const bySerial: Record<string, ObjectStatusValue> = {};

  objects?.forEach((object) => {
    if (object?.uniqueId) {
      byUniqueId[object.uniqueId] = object.status;
    }
    [object?.name, object?.aNumber].forEach((key) => {
      indexStatusByKey(byName, key, object.status);
    });
    indexStatusByKey(bySerial, object?.serialNumber, object.status);
  });

  return { byUniqueId, byName, bySerial };
};

export const resolveReportObjectInactive = (
  row: ReportInactiveRow | null | undefined,
  lookups: ReportObjectStatusLookups
): boolean => {
  if (!row) return false;
  if (typeof row.isInactive === "boolean") return row.isInactive;

  // Only treat row.status as object-level when it is clearly DEACTIVATED.
  // Ride statuses like "Active"/"Activiteit" must not short-circuit catalog join.
  if (
    row.status !== undefined &&
    row.status !== null &&
    isInactiveObject(row.status)
  ) {
    return true;
  }

  const uniqueId = row.uniqueId || row.groupedInfo?.uniqueId;
  if (
    uniqueId &&
    Object.prototype.hasOwnProperty.call(lookups.byUniqueId, uniqueId)
  ) {
    return isInactiveObject(lookups.byUniqueId[uniqueId]);
  }

  // Prefer serial/device before name so a deactivated serial cannot tag an
  // active object whose name (or aNumber) happens to equal that serial string.
  const serialStatus = lookupStatusByCandidates(
    [row.device, row.serialNumber, row.groupedInfo?.serialNumber],
    lookups.bySerial
  );
  if (serialStatus !== undefined) {
    return isInactiveObject(serialStatus);
  }

  const nameStatus = lookupStatusByCandidates(
    [
      row.name,
      row.object,
      row.groupedInfo?.name,
      row.licensePlate,
      row.aNumber
    ],
    lookups.byName
  );
  if (nameStatus !== undefined) {
    return isInactiveObject(nameStatus);
  }

  return false;
};

export const enrichReportRowsWithInactive = (
  rows: ReportInactiveRow[] | null | undefined,
  lookups: ReportObjectStatusLookups
): Array<ReportInactiveRow & { isInactive: boolean }> => {
  if (!rows?.length) return [];

  return rows.map((row) => ({
    ...row,
    isInactive: resolveReportObjectInactive(row, lookups)
  }));
};

export const filterInactiveReportRows = <T extends { isInactive?: boolean }>(
  rows: T[] | null | undefined,
  showInactiveObjects: boolean
): T[] => {
  if (!rows?.length) return [];
  if (showInactiveObjects) return rows;
  return rows.filter((row) => !row.isInactive);
};

export const applyReportInactiveFilter = (
  rows: any[] | null | undefined,
  lookups: ReportObjectStatusLookups,
  showInactiveObjects: boolean,
  statusLookupsReady: boolean = true
): any[] => {
  if (!statusLookupsReady) {
    return rows ?? [];
  }

  return filterInactiveReportRows(
    enrichReportRowsWithInactive(rows, lookups),
    showInactiveObjects
  );
};

/** Object-listing report types that support the inactive-objects FE filter. */
export const REPORT_TYPES_WITH_INACTIVE_OBJECTS = [2, 3, 4, 6, 7, 10] as const;

export const supportsReportInactiveObjectsToggle = (
  reportType?: number | null
): boolean =>
  REPORT_TYPES_WITH_INACTIVE_OBJECTS.includes(
    Number(reportType) as (typeof REPORT_TYPES_WITH_INACTIVE_OBJECTS)[number]
  );

/** Client-only filter fields that must never be sent to POST /reports or export. */
export const CLIENT_ONLY_REPORT_FILTER_KEYS = ["showInactiveObjects"] as const;

export const stripClientOnlyReportFilterFields = <
  T extends Record<string, any>
>(
  payload: T | null | undefined
): Omit<T, (typeof CLIENT_ONLY_REPORT_FILTER_KEYS)[number]> => {
  if (!payload || typeof payload !== "object") {
    return {} as Omit<T, (typeof CLIENT_ONLY_REPORT_FILTER_KEYS)[number]>;
  }

  const next = { ...payload };
  CLIENT_ONLY_REPORT_FILTER_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
};
